import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { MagnifyingGlass, PaperPlaneTilt, Certificate } from "@phosphor-icons/react";
import NewPacketModal from "@/components/NewPacketModal";
import { PageHeader } from "./shared";
import { WorkerAvailability } from "@/components/WorkerAvailability";

const today = new Date().toISOString().slice(0, 10);

export default function SalesWorkers() {
  const { autoSend } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [packetFor, setPacketFor] = useState(null);
  useEffect(() => { api.get("/sales/workers").then((r) => setRows(Array.isArray(r.data) ? r.data : [])); }, []);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows.filter((w) => (status === "all" || (w.status || "active") === status)
      && (!s || [w.name, w.address, ...(w.certs || []).map((c) => c.title), ...(w.current_projects || []), ...(w.current_assignments || []).map((a) => a.rep_name)].filter(Boolean).some((v) => String(v).toLowerCase().includes(s))));
  }, [rows, q, status]);
  const nActive = rows.filter((w) => (w.status || "active") === "active").length;

  return (
    <div data-testid="sales-workers">
      <PageHeader eyebrow="Bench" title="Workers" blurb="Every worker on file — active and inactive — with their certifications and where they're working right now. For anyone currently assigned you'll see when that job is expected to wrap and which rep to call about it. Workers the office has flagged Do Not Use are left out. Contact details only; no pay or tax data."
        action={
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-3 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="sales-workers-search" placeholder="Search name, city, cert, project…"
                   className="h-10 pl-9 pr-3 w-72 border border-slate-300 rounded-md bg-white text-sm" />
          </div>
        } />

      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="sales-workers-filters">
        {[["all", "All", rows.length], ["active", "Active", nActive], ["inactive", "Inactive", rows.length - nActive]].map(([k, label, n]) => (
          <button key={k} type="button" onClick={() => setStatus(k)} data-testid={`sales-workers-filter-${k}`}
                  className={`h-9 px-3 rounded-full text-sm font-semibold border inline-flex items-center gap-1.5 ${status === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
            {label} <span className={`text-[11px] px-1.5 rounded-full ${status === k ? "bg-white/20" : "bg-slate-100 text-slate-600"}`}>{n}</span>
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Worker</th>
              <th className="text-left px-4 py-3">Contact</th>
              <th className="text-left px-4 py-3">Based</th>
              <th className="text-left px-4 py-3">Certifications</th>
              <th className="text-left px-4 py-3">Availability · current assignment</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">{rows.length ? "No one matches." : "No workers on file."}</td></tr>}
            {shown.map((w) => {
              const inactive = (w.status || "active") !== "active";
              const free = !inactive && !(w.current_projects || []).length;
              return (
                <tr key={w.id} className={`border-t border-slate-100 hover:bg-slate-50 ${inactive ? "text-slate-500" : ""}`} data-testid={`sales-worker-${w.id}`}>
                  <td className="px-4 py-3 font-semibold">{w.name}
                    {free && <span className="ml-2 inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-emerald-100 text-emerald-800 border-emerald-300">available</span>}
                    {inactive && <span className="ml-2 inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-300" data-testid={`sales-worker-inactive-${w.id}`}>inactive</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{w.phone && <div>{w.phone}</div>}{w.email && <div className="truncate max-w-[200px]">{w.email}</div>}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs max-w-[200px]">{w.address || "—"}</td>
                  <td className="px-4 py-3">
                    {(w.certs || []).length === 0 && <span className="text-xs text-slate-400">—</span>}
                    <div className="flex flex-wrap gap-1">
                      {(w.certs || []).map((c, i) => {
                        const expired = c.expires_on && c.expires_on < today;
                        return (
                          <span key={i} title={c.expires_on ? `${expired ? "Expired" : "Expires"} ${c.expires_on}` : "No expiration"}
                                className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border ${expired ? "bg-rose-50 text-rose-700 border-rose-200 line-through" : "bg-slate-50 text-slate-700 border-slate-200"}`}>
                            <Certificate size={11} /> {c.title}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-4 py-3"><WorkerAvailability assignments={w.current_assignments} testId={`sales-worker-avail-${w.id}`} /></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setPacketFor(w)} data-testid={`sales-worker-packet-${w.id}`} title="Draft an onboarding packet for one of your projects"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded border border-emerald-300 text-emerald-800 hover:bg-emerald-50 text-xs font-bold">
                      <PaperPlaneTilt size={13} weight="bold" /> Send packet
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {packetFor && <NewPacketModal mode="sales" autoSend={autoSend} initial={{ worker_id: packetFor.id, worker_email: packetFor.email || "", worker_name: packetFor.name, worker_phone: packetFor.phone || "" }}
                                    onClose={() => setPacketFor(null)} />}
    </div>
  );
}
